import React from "react";
//Limitless imports
import { useDevLogging, useNotificationContext } from "@limitless/common/hooks";
import { getGraphQLClient, handleSecureAjaxError, useSessionErrorContext } from "../..";
const START_LOADING = "start-loading";
const STOP_LOADING = "stop-loading";
const RESET = "reset-api-status";
const UPDATE_RESULT = "update-api-result";
const SET_ERROR_MESSAGE = "set-error-message";
const INITIAL_STATE = {
  loading: false,
  rows: [],
  data: {},
  errorMessage: "",
  dataKey: null
};
const apiStatusReducer = (state, action) => {
  switch (action.type) {
    case START_LOADING:
      return {
        ...state,
        loading: true,
        errorMessage: ""
      };
    case STOP_LOADING:
      return {
        ...state,
        loading: false
      };
    case UPDATE_RESULT:
      {
        const {
          result,
          dataKey
        } = action.payload;
        if (Array.isArray(result)) {
          return {
            ...state,
            dataKey,
            loading: false,
            rows: result,
            errorMessage: ""
          };
        }
        return {
          ...state,
          dataKey,
          loading: false,
          data: result,
          errorMessage: ""
        };
      }
    case SET_ERROR_MESSAGE:
      {
        return {
          loading: false,
          rows: [],
          errorMessage: action.payload
        };
      }
    case RESET:
      return INITIAL_STATE;
    default:
      throw new Error(`Unsupport action type ${action.type} for userManagerAPIHook`);
  }
};
export const useGraphAPIHook = (name = "last") => {
  const [apiStatus, dispatch] = React.useReducer(apiStatusReducer, INITIAL_STATE);
  const {
    addErrorAndNotify
  } = useSessionErrorContext();
  const {
    addSuccessNotification
  } = useNotificationContext();
  const {
    stateTracker
  } = useDevLogging();
  stateTracker.trackState({
    api: {
      [name]: apiStatus
    }
  });
  const processError = (error, options) => {
    const {
      functionName,
      notifyDetails
    } = options;
    handleSecureAjaxError(error, functionName);
    if (notifyDetails) {
      const {
        actionName,
        errorMessage = "Platform error",
        errorNotification
      } = notifyDetails;
      addErrorAndNotify(error, null, actionName, errorNotification || errorMessage);
    }
    stopLoading();
  };
  const processResponse = (response, options) => {
    const {
      dataKey
    } = options;
    const result = typeof dataKey === "function" ? dataKey(response.data) : response.data[dataKey];
    dispatch({
      type: UPDATE_RESULT,
      payload: {
        result,
        dataKey
      }
    });
    return result;
  };
  const fetchData = options => {
    return new Promise((resolve, reject) => {
      startLoading();
      const {
        query,
        variables,
        fetchPolicy = "no-cache"
      } = options;
      const graphQLClient = getGraphQLClient();
      graphQLClient.query({
        query,
        variables,
        fetchPolicy
      }).then(response => {
        resolve(processResponse(response, options));
      }).catch(error => {
        processError(error, options);
        reject(error);
      });
    });
  };
  const mutateData = options => {
    return new Promise((resolve, reject) => {
      startLoading();
      const {
        mutation,
        variables,
        dataKey,
        notifyDetails,
        postSuccessFunction,
        finallyFunction
      } = options;
      const graphQLClient = getGraphQLClient();
      graphQLClient.mutate({
        mutation,
        variables
      }).then(response => {
        const result = typeof dataKey === "function" ? dataKey(response.data) : response.data[dataKey];
        dispatch({
          type: UPDATE_RESULT,
          payload: {
            result,
            dataKey
          }
        });
        if (notifyDetails !== null && notifyDetails !== void 0 && notifyDetails.successNotification) {
          addSuccessNotification(notifyDetails.successNotification);
        }
        if (typeof postSuccessFunction === "function") {
          postSuccessFunction();
        }
        if (typeof finallyFunction === "function") {
          finallyFunction(result);
        }
        resolve(result);
      }).catch(error => {
        processError(error, options);
        if (typeof finallyFunction === "function") {
          finallyFunction();
        }
        reject(error);
      });
    });
  };
  const startLoading = () => dispatch({
    type: START_LOADING
  });
  const stopLoading = () => dispatch({
    type: STOP_LOADING
  });
  const setErrorMessage = () => dispatch({
    type: SET_ERROR_MESSAGE
  });
  const reset = () => dispatch({
    type: RESET
  });
  return {
    apiStatus,
    fetchData,
    mutateData,
    startLoading,
    stopLoading,
    setErrorMessage,
    reset
  };
};